import React, { useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, RotateCcw, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const platforms = [
  { id: 'amazon_ind', name: 'Amazon India' },
  { id: 'amazon_uae', name: 'Amazon UAE' },
  { id: 'amazon_ksa', name: 'Amazon KSA' },
  { id: 'amazon_au', name: 'Amazon Australia' },
  { id: 'noon_uae', name: 'Noon.com UAE' },
  { id: 'myntra', name: 'Myntra' },
  { id: 'ajio', name: 'Ajio' },
  { id: 'flipkart', name: 'Flipkart' },
  { id: 'offline', name: 'Offline Store' },
];

export default function UploadPage() {
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadType, setUploadType] = useState('sales'); // 'sales', 'returns', or 'omsguru'
  const { toast } = useToast();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setUploadedFile(acceptedFiles[0]);
        setUploadResult(null);
      }
    },
  });

  const handleUpload = async () => {
    // OMSguru doesn't need platform selection
    if (uploadType !== 'omsguru' && !selectedPlatform) {
      toast({
        title: 'Error',
        description: 'Please select a platform',
        variant: 'destructive',
      });
      return;
    }
    
    if (!uploadedFile) {
      toast({
        title: 'Error',
        description: 'Please upload a file',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      
      let endpoint;
      if (uploadType === 'omsguru') {
        endpoint = `${API}/sales/upload-omsguru`;
      } else if (uploadType === 'returns') {
        formData.append('platform', selectedPlatform);
        endpoint = `${API}/sales/upload-returns`;
      } else {
        formData.append('platform', selectedPlatform);
        endpoint = `${API}/sales/upload`;
      }
      
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult({
        success: true,
        message: response.data.message,
        count: response.data.count || response.data.total_records,
        date_range: response.data.date_range,
        total_units_returned: response.data.total_units_returned,
        note: response.data.note,
        platform_breakdown: response.data.platform_breakdown,
        sales_count: response.data.sales_count,
        returns_count: response.data.returns_count
      });

      let successMessage;
      if (uploadType === 'omsguru') {
        successMessage = `Processed ${response.data.total_records} records from ${response.data.platform_breakdown?.length || 0} platforms`;
      } else if (uploadType === 'returns') {
        successMessage = `Uploaded ${response.data.count} returns records (${response.data.total_units_returned} units)`;
      } else {
        successMessage = `Uploaded ${response.data.count} sales records${response.data.date_range ? ` (${response.data.date_range})` : ''}`;
      }

      toast({
        title: 'Success',
        description: successMessage,
      });

      setUploadedFile(null);
      setSelectedPlatform('');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already been uploaded')) {
        setUploadResult({
          success: false,
          message: error.response.data.detail,
        });

        toast({
          title: 'Duplicate Upload Prevented',
          description: 'This file has already been uploaded',
          variant: 'destructive',
        });
      } else {
        setUploadResult({
          success: false,
          message: error.response?.data?.detail || 'Upload failed',
        });

        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Upload failed',
          variant: 'destructive',
        });
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">Upload Sales Data</h1>
          <p className="text-muted-foreground mt-2">Import sales or returns data from various platforms</p>
        </div>

        <div className="max-w-3xl space-y-6">
          {/* Upload Type Toggle */}
          <div>
            <label className="block text-sm font-medium mb-3">Upload Type *</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => { setUploadType('sales'); setSelectedPlatform(''); }}
                data-testid="upload-type-sales"
                className={`p-4 rounded-sm border text-sm font-medium transition-colors ${
                  uploadType === 'sales'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                <Upload className="w-5 h-5 mx-auto mb-2" />
                Sales Data
              </button>
              <button
                onClick={() => { setUploadType('returns'); setSelectedPlatform(''); }}
                data-testid="upload-type-returns"
                className={`p-4 rounded-sm border text-sm font-medium transition-colors ${
                  uploadType === 'returns'
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                <RotateCcw className="w-5 h-5 mx-auto mb-2" />
                Returns Data
              </button>
              <button
                onClick={() => { setUploadType('omsguru'); setSelectedPlatform(''); }}
                data-testid="upload-type-omsguru"
                className={`p-4 rounded-sm border text-sm font-medium transition-colors ${
                  uploadType === 'omsguru'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                <Layers className="w-5 h-5 mx-auto mb-2" />
                OMSguru
              </button>
            </div>
            {uploadType === 'returns' && (
              <p className="text-xs text-orange-600 mt-2">
                Returns will be subtracted from sales for velocity calculations
              </p>
            )}
            {uploadType === 'omsguru' && (
              <p className="text-xs text-blue-600 mt-2">
                Upload multi-platform data from OMSguru (all platforms in one file)
              </p>
            )}
          </div>

          {/* Platform selection - hidden for OMSguru */}
          {uploadType !== 'omsguru' && (
          <div>
            <label className="block text-sm font-medium mb-3">Select Platform *</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {platforms.map((platform) => (
                <button
                  key={platform.id}
                  onClick={() => setSelectedPlatform(platform.id)}
                  data-testid={`platform-${platform.id}`}
                  className={`p-3 rounded-sm border text-sm font-medium transition-colors ${
                    selectedPlatform === platform.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-border hover:bg-muted'
                  }`}
                >
                  {platform.name}
                </button>
              ))}
            </div>
          </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-3">
              Upload File (CSV or Excel) *
            </label>
            <div
              {...getRootProps()}
              data-testid="dropzone"
              className={`border-2 border-dashed rounded-sm p-12 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-primary bg-primary/5'
                  : uploadType === 'returns'
                  ? 'border-orange-300 hover:border-orange-500 hover:bg-orange-50/50'
                  : uploadType === 'omsguru'
                  ? 'border-blue-300 hover:border-blue-500 hover:bg-blue-50/50'
                  : 'border-border hover:border-primary/50 hover:bg-muted/50'
              }`}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center gap-4">
                {uploadedFile ? (
                  <>
                    <FileSpreadsheet className={`w-12 h-12 ${uploadType === 'returns' ? 'text-orange-600' : uploadType === 'omsguru' ? 'text-blue-600' : 'text-primary'}`} strokeWidth={1.5} />
                    <div>
                      <p className="font-medium" data-testid="uploaded-file-name">{uploadedFile.name}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {(uploadedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">Click or drag to replace</p>
                  </>
                ) : (
                  <>
                    {uploadType === 'returns' ? (
                      <RotateCcw className="w-12 h-12 text-orange-400" strokeWidth={1.5} />
                    ) : uploadType === 'omsguru' ? (
                      <Layers className="w-12 h-12 text-blue-400" strokeWidth={1.5} />
                    ) : (
                      <Upload className="w-12 h-12 text-muted-foreground" strokeWidth={1.5} />
                    )}
                    <div>
                      <p className="font-medium">
                        {uploadType === 'omsguru' ? 'Drop your OMSguru export file here' : `Drop your ${uploadType === 'returns' ? 'returns' : 'sales'} file here`}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        or click to browse
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">Supports CSV, XLS, XLSX files</p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={`rounded-sm p-4 border ${
            uploadType === 'returns' ? 'bg-orange-50/50 border-orange-200' : 
            uploadType === 'omsguru' ? 'bg-blue-50/50 border-blue-200' : 
            'bg-muted/50 border-border'
          }`}>
            <h3 className="text-sm font-medium mb-2">
              {uploadType === 'returns' ? 'Returns File Format' : 
               uploadType === 'omsguru' ? 'OMSguru Export Format' : 
               'File Format Guidelines'}
            </h3>
            {uploadType === 'returns' ? (
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• File should contain columns for SKU/MSKU/ASIN and Quantity</li>
                <li>• Date column is optional (defaults to today if not provided)</li>
                <li>• Column names like "Return Quantity", "Quantity", "SKU" work automatically</li>
                <li>• Returns will be subtracted from sales in forecasting calculations</li>
              </ul>
            ) : uploadType === 'omsguru' ? (
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Upload the order_info.csv export from OMSguru</li>
                <li>• <strong>Supported platforms:</strong> Amazon UAE, KSA, AU, India, Flipkart, Myntra, Offline</li>
                <li>• Automatically separates sales (Delivered/Shipped) from returns</li>
                <li>• Returns are subtracted from sales for accurate velocity calculations</li>
                <li>• <strong>Note:</strong> Ajio and Noon data not included - upload separately</li>
              </ul>
            ) : (
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• File should contain columns for SKU/Variant, Quantity, and optionally Price and Date</li>
                <li>• Column names can vary (e.g., "SKU", "Variant", "Product Code")</li>
                <li>• The system will automatically detect and map columns</li>
                <li>• Rows with missing SKU or zero quantity will be skipped</li>
              </ul>
            )}
          </div>

          <Button
            onClick={handleUpload}
            data-testid="upload-button"
            disabled={(uploadType !== 'omsguru' && !selectedPlatform) || !uploadedFile || uploading}
            className={`w-full ${
              uploadType === 'returns' ? 'bg-orange-600 hover:bg-orange-700' : 
              uploadType === 'omsguru' ? 'bg-blue-600 hover:bg-blue-700' : 
              ''
            }`}
          >
            {uploading ? 'Processing...' : 
             uploadType === 'returns' ? 'Upload Returns Data' : 
             uploadType === 'omsguru' ? 'Upload OMSguru Data' : 
             'Upload Sales Data'}
          </Button>

          {uploadResult && (
            <div
              data-testid="upload-result"
              className={`border rounded-sm p-4 flex items-start gap-3 ${
                uploadResult.success
                  ? 'border-success/30 bg-success/5'
                  : 'border-destructive/30 bg-destructive/5'
              }`}
            >
              {uploadResult.success ? (
                <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
              ) : (
                <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" strokeWidth={2} />
              )}
              <div className="flex-1">
                <p className="font-medium text-sm">
                  {uploadResult.success ? 'Upload Successful' : 'Upload Failed'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">{uploadResult.message}</p>
                {uploadResult.count && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {uploadResult.count} records processed
                    {uploadResult.date_range && ` • Date range: ${uploadResult.date_range}`}
                  </p>
                )}
                {/* OMSguru platform breakdown */}
                {uploadResult.platform_breakdown && uploadResult.platform_breakdown.length > 0 && (
                  <div className="mt-3 p-3 bg-muted/50 rounded-sm">
                    <p className="text-xs font-medium mb-2">Platform Breakdown:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {uploadResult.platform_breakdown.map((p, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className="text-muted-foreground">{p.platform}:</span>
                          <span className="font-medium">
                            {p.sales} sales, {p.returns} returns
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {uploadResult.note && (
                  <p className={`text-xs mt-2 ${uploadType === 'omsguru' ? 'text-blue-600' : 'text-orange-600'}`}>
                    {uploadResult.note}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}